import PropTypes from 'prop-types';
import {
  Box,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Typography,
} from '@mui/material';
import { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { CommonStyles } from '../CommonStyles';
import { INVALID_FORMAT_ERR, ORIENTAL_ORANGE, RED_COLOR, REQUIRED } from '../../../constants';
import { commonTernary, getRoot, isWebComponentFlow, setSSNError } from '../../commonCode';
import LeadGenerationContext from '../../../context/LeadGenerationContext';
import CustomTooltip from '../../Tooltip/CustomTooltip';
import { HideEyeIcon, ShowEyeIcon } from '../../../images/svg/CustomizedSVG';

export const SSNAndroid = (props) => {
  const { t } = useTranslation();
  const baseContext = isWebComponentFlow();
  const classes = CommonStyles(baseContext?.theme);
  const {
    handleKeyDown,
    error,
    ssn,
    isCommercialPage,
    id,
    disabled,
    isFlowTypeETB,
    whySSN,
    isBundle = false,
  } = props;
  const { theme } = baseContext;
  const commonStyles = CommonStyles(baseContext?.theme);

  const [ssnVal, setSsnVal] = useState(true);
  const [ssnFakeValue, setSsnFakeValue] = useState('');
  const [showIcon, setShowIcon] = useState(false);
  const [ssnAltText, setSsnAltText] = useState(
    <HideEyeIcon color={theme?.palette?.orientalPrimary?.main} />
  );

  const showHideHandler = (handler) => {
    if (handler === 'click') {
      setShowIcon((prev) => !prev);
      setSsnVal((prev) => !prev);
    } else if (handler === 'blur') {
      setShowIcon(false);
      setSsnVal(true);
    }
  };

  useEffect(() => {
    const len = ssn?.length;
    let stars;
    switch (len) {
      case 1:
        stars = 'X';
        break;
      case 2:
        stars = 'XX';
        break;
      case 3:
        stars = 'XXX';
        break;
      case 4:
        stars = 'XXX-';
        break;
      case 5:
        stars = 'XXX-X';
        break;
      case 6:
        stars = 'XXX-XX';
        break;
      case 7:
        stars = 'XXX-XX-';
        break;
      case 0:
        stars = '';
        break;
      default:
        stars = 'XXX-XX-';
        break;
    }
    const ssnSubstring = ssn?.substring(7);
    const result = stars + ssnSubstring;
    setSsnFakeValue(result);
  }, [ssn]);

  useEffect(() => {
    if (showIcon) {
      setSsnAltText('Show SSN number');
    } else setSsnAltText('Hide SSN number');
  }, [showIcon]);

  useEffect(() => {
    if (ssn?.length === 11) {
      setShowIcon(false);
      setSsnVal(true);
    }
  }, [ssn]);

  function areAllNumbersSameInSSN(ssnWithHyphens) {
    const newSSNNumber = ssnWithHyphens.replace(/-/g, '');
    if (newSSNNumber && newSSNNumber.length === 9) {
      const numbersArray = Array.from(newSSNNumber);
      const firstNumber = numbersArray[0];
      return numbersArray.every((number) => number === firstNumber);
    }
    return false;
  }

  const handleKeyBlur = (e) => {
    if (e?.relatedTarget === null || e?.relatedTarget?.id !== 'ssnHideShowBtn') {
      showHideHandler('blur');
    }
    if (!ssn || ssn.length !== 11) return;
    const result = areAllNumbersSameInSSN(ssn);
    if (result) {
      setSSNError(baseContext, id, INVALID_FORMAT_ERR);
    } else {
      setSSNError(baseContext, id, '');
    }
  };

  const ssnField = () => {
    let jsx;
    if (isCommercialPage) {
      jsx = (
        <span>
          {t('Social Security Number Commercial', {
            defaultValue: 'Social Security Number',
          })}

          <span
            style={{
              color: ORIENTAL_ORANGE,
              position: 'absolute',
              fontSize: '22px',
              marginLeft: '4px',
            }}
          >
            *
          </span>
        </span>
      );
    } else if (isFlowTypeETB) {
      jsx = (
        <span>
          {t('Social security number', {
            defaultValue: 'Social Security Number',
          })}

          <span
            style={{
              color: ORIENTAL_ORANGE,
              position: 'absolute',
              fontSize: '22px',
              marginLeft: '4px',
            }}
          >
            *
          </span>
        </span>
      );
    } else if (isBundle && baseContext.currentPage === 'personal-info') {
      jsx = (
        <span
          style={{
            color: error ? 'red' : 'inherit',
          }}
        >
          {t('SSN', { defaultValue: 'SSN' })}
        </span>
      );
    } else if (isBundle) {
      jsx = (
        <span
          style={{
            color: error ? 'red' : 'inherit',
          }}
        >
          {t('Social Security Number', { defaultValue: 'Social Security Number' })}
        </span>
      );
    } else {
      jsx = (
        <span>
          {t('Social Security Number', {
            defaultValue: 'Social Security Number',
          })}

          <span
            style={{
              color: ORIENTAL_ORANGE,
              position: 'absolute',
              fontSize: '22px',
              marginLeft: '4px',
            }}
          >
            *
          </span>
        </span>
      );
    }
    return jsx;
  };

  return (
    <>
      <InputLabel
        htmlFor="ssn"
        aria-label={`${t('Social Security Number', {
          defaultValue: 'Social Security Number',
        })} ${t(REQUIRED, { defaultValue: REQUIRED })}`}
        sx={classes.label_title}
      >
        {whySSN ? (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>
              {t('Social Security Number', {
                defaultValue: 'Social Security Number',
              })}

              <span
                style={{
                  color: ORIENTAL_ORANGE,
                  position: 'absolute',
                  fontSize: '22px',
                  marginLeft: '4px',
                }}
              >
                *
              </span>
            </span>
            <Box component="span" sx={classes.ssnTooltip}>
              <CustomTooltip
                custom
                customAriaLabel={`${t('ssnTooltip', {
                  defaultValue: 'ssnTooltip',
                })} ${t('More Info', { defaultValue: 'More Info' })}`}
                placement="top"
                text={t(`Politically Exposed Tooltip`, {
                  defaultValue: `The term "politically exposed person" generally includes a current or former senior foreign political figure, their immediate family, and their close associates.`,
                })}
              />
              <Typography sx={classes.ssnTextTooltip}>
                {t('Why do we need your SSN?', {
                  defaultValue: 'Why do we need your SSN?',
                })}
              </Typography>
            </Box>
          </div>
        ) : (
          ssnField()
        )}
      </InputLabel>
      <OutlinedInput
        id={id}
        name="ssn"
        sx={[
          commonStyles.input,
          ...commonTernary(
            disabled,
            [commonStyles.outlined_inputs, commonStyles.outlined_inputs_disabled],
            [commonStyles.outlined_inputs, { border: `1px solid ${error ? RED_COLOR : '#E2E2E2'}` }]
          ),
          ...(isBundle
            ? [
                commonStyles.customBorderRadius,
                commonStyles.outlined_input_bundle,
                {
                  backgroundColor: error ? '#D5151F1A' : theme?.palette?.fieldsBackground?.main,
                  border: error
                    ? `2px solid ${theme?.palette?.danger?.main}`
                    : `1px solid ${theme?.palette?.fieldsBorder?.main}`,
                  '&.Mui-focused': {
                    border: error ? `1px solid ${theme?.palette?.primary?.main}` : '',
                  },
                },
                disabled && {
                  backgroundColor: `${theme?.palette?.disabledAddress?.main} !important`,
                  color: theme?.palette?.disabled?.main,
                  WebkitTextFillColor: theme?.palette?.disabled?.main,
                },
              ]
            : []),
          {
            '& .MuiInputBase-input': {
              fontSize: '16px',
            },
            '& .MuiInputBase-input::placeholder': {
              fontSize: '16px',
            },
          },
        ]}
        placeholder={
          isBundle
            ? 'XXX - XX -'
            : !isCommercialPage
            ? t('Enter social security number', {
                defaultValue: 'Enter social security number',
              })
            : t('Enter social security number commercial', {
                defaultValue: 'Enter social security number',
              })
        }
        fullWidth
        autoComplete="off"
        disabled={!!disabled}
        onBlur={handleKeyBlur}
        value={ssnVal ? ssnFakeValue : ssn}
        onChange={handleKeyDown}
        inputProps={{
          maxLength: 11,
          inputMode: 'numeric',
          'data-testid': 'SSNAndroid',
        }}
        style={{
          notchedOutline: !error ? commonStyles.notchedOutline : commonStyles.notchedOutlineErr,
        }}
        slotProps={{
          notchedOutline: {
            sx: commonStyles.noBorder,
          },
          input: {
            sx: disabled && {
              WebkitTextFillColor: 'unset !important',
            },
          },
        }}
        endAdornment={
          <InputAdornment
            position="end"
            style={{ marginLeft: '0', marginRight: '-7px', maxWidth: '35px' }}
          >
            <IconButton
              disableFocusRipple
              id="ssnHideShowBtn"
              data-testid="shouHideBtn_id"
              onClick={() => {
                showHideHandler('click');
                getRoot().getElementById(id)?.focus();
              }}
              tabIndex="0"
              aria-label={showIcon ? 'show social security number' : 'hide social security number'}
              aria-roledescription="button"
            >
              {showIcon ? (
                <ShowEyeIcon color={theme?.palette?.orientalPrimary?.main} />
              ) : (
                <HideEyeIcon color={theme?.palette?.orientalPrimary?.main} />
              )}
            </IconButton>
          </InputAdornment>
        }
      />
    </>
  );
};

SSNAndroid.propTypes = {
  handleKeyDown: PropTypes.func,
  error: PropTypes.string,
  ssn: PropTypes.string,
  isCommercialPage: PropTypes.bool,
  id: PropTypes.string,
  disabled: PropTypes.bool,
  isFlowTypeETB: PropTypes.bool,
  whySSN: PropTypes.bool,
  isBundle: PropTypes.bool,
};
